'use client';

import React, { useMemo } from 'react';
import { PermissionTableProps } from './types';
import {
  getAction,
  getResourceAndScope,
  getPermissionGroupCategory,
  ACTION_PRIORITY,
} from './utils';
import ExportPermissionsSection from './components/ExportPermissionsSection';
import PermissionGroup from './components/PermissionGroup';

/**
 * PermissionTable component for managing role permissions
 */
const PermissionTable: React.FC<PermissionTableProps> = ({
  permissionGroups,
  selectedPermissions,
  onSelectPermission,
}) => {
  // Extract all export permissions
  const exportPermissions = useMemo(() => {
    const result: {
      resource: string;
      scope: string;
      permissionCode: string;
      description: string;
    }[] = [];

    permissionGroups.forEach((group) => {
      group.permissions.forEach((permission) => {
        const action = getAction(permission.code);
        if (action === 'export') {
          const { resource, scope } = getResourceAndScope(permission.code);
          result.push({
            resource,
            scope,
            permissionCode: permission.code,
            description: permission.description,
          });
        }
      });
    });

    // Sort by resource and then by scope
    return result.sort((a, b) => {
      const resourceCompare = a.resource.localeCompare(b.resource);
      if (resourceCompare !== 0) return resourceCompare;
      return a.scope.localeCompare(b.scope);
    });
  }, [permissionGroups]);

  // Filter out export permissions and sort permission groups
  const sortedPermissionGroups = useMemo(() => {
    // First, filter out export permissions from each group
    const filteredGroups = permissionGroups
      .map((group) => {
        // Filter out export permissions
        const filteredPermissions = group.permissions.filter((permission) => {
          const action = getAction(permission.code);
          return action !== 'export';
        });

        // Return a new group with filtered permissions
        return {
          ...group,
          permissions: filteredPermissions,
        };
      })
      .filter((group) => group.permissions.length > 0); // Remove groups with no permissions left

    // Then sort the filtered groups
    const sortedGroups = filteredGroups.sort((a, b) => {
      // Check if either group contains special resources
      const aHasSpecialResources = a.permissions.some((permission) => {
        const { resource } = getResourceAndScope(permission.code);
        return (
          resource === 'internal_banner' ||
          resource === 'reimbursement' ||
          resource === 'map'
        );
      });

      const bHasSpecialResources = b.permissions.some((permission) => {
        const { resource } = getResourceAndScope(permission.code);
        return (
          resource === 'internal_banner' ||
          resource === 'reimbursement' ||
          resource === 'map'
        );
      });

      // If one has special resources and the other doesn't, sort accordingly
      if (aHasSpecialResources && !bHasSpecialResources) {
        return 1; // a comes after b
      }
      if (!aHasSpecialResources && bHasSpecialResources) {
        return -1; // a comes before b
      }

      // Otherwise, sort by category
      const categoryA = getPermissionGroupCategory(a);
      const categoryB = getPermissionGroupCategory(b);

      if (categoryA !== categoryB) {
        return categoryA - categoryB;
      }

      // If same category, sort alphabetically by group name
      return a.group.localeCompare(b.group);
    });

    // Custom sorting to ensure specific groups are side by side
    const finalGroups: typeof sortedGroups = [];
    const groupMap = new Map<string, typeof sortedGroups[0]>();

    // Create a map of group names to group objects
    sortedGroups.forEach((group) => {
      groupMap.set(group.group.toLowerCase(), group);
    });

    // Process all groups
    const processedGroups = new Set<string>();

    // Helper function to add a group and its pair if available
    const addGroupWithPair = (groupName: string, pairName: string) => {
      const lowerGroupName = groupName.toLowerCase();
      const lowerPairName = pairName.toLowerCase();

      if (
        groupMap.has(lowerGroupName) &&
        !processedGroups.has(lowerGroupName)
      ) {
        const group = groupMap.get(lowerGroupName);
        if (group) finalGroups.push(group);
        processedGroups.add(lowerGroupName);

        // Add its pair if available
        if (
          groupMap.has(lowerPairName) &&
          !processedGroups.has(lowerPairName)
        ) {
          const pairGroup = groupMap.get(lowerPairName);
          if (pairGroup) finalGroups.push(pairGroup);
          processedGroups.add(lowerPairName);
        }
      }
    };

    // Add non-special groups first (excluding Users and Roles)
    sortedGroups.forEach((group) => {
      const lowerGroupName = group.group.toLowerCase();
      const hasSpecialResources = group.permissions.some((permission) => {
        const { resource } = getResourceAndScope(permission.code);
        return (
          resource === 'internal_banner' ||
          resource === 'reimbursement' ||
          resource === 'map'
        );
      });

      const isUserOrRole =
        lowerGroupName === 'users' || lowerGroupName === 'roles';

      if (
        !hasSpecialResources &&
        !isUserOrRole &&
        !processedGroups.has(lowerGroupName)
      ) {
        finalGroups.push(group);
        processedGroups.add(lowerGroupName);
      }
    });

    // Ensure users and roles are side by side at the end of the regular section
    addGroupWithPair('Users', 'Roles');
    addGroupWithPair('Roles', 'Users');

    // Ensure internal_banner, reimbursement, and map are at the end and side by side
    addGroupWithPair('Internal Banner', 'Reimbursement');
    addGroupWithPair('Reimbursement', 'Map');
    addGroupWithPair('Map', 'Internal Banner');

    // Add any remaining groups
    sortedGroups.forEach((group) => {
      const lowerGroupName = group.group.toLowerCase();
      if (!processedGroups.has(lowerGroupName)) {
        finalGroups.push(group);
        processedGroups.add(lowerGroupName);
      }
    });

    return finalGroups;
  }, [permissionGroups]);

  return (
    <div
      className="flex flex-col flex-1 overflow-y-auto overflow-x-hidden px-2"
      data-testid="permission-table"
    >
      <div className="grid grid-cols-1 min-[1015px]:grid-cols-2 gap-4 pb-4">
        {sortedPermissionGroups.map((group) => {
          // Get unique applicable actions for this group
          const actions = group.permissions.reduce(
            (acc: Set<string>, permission) => {
              const segments = permission.code.split('.');
              const action = segments[0] || 'other'; // Use 'other' if no action segment
              acc.add(action);
              return acc;
            },
            new Set<string>()
          );

          // Sort actions by priority
          const groupApplicableActions = Array.from(actions).sort(
            (a, b) => (ACTION_PRIORITY[a] || 99) - (ACTION_PRIORITY[b] || 99)
          );

          // Group permissions by resource and scope
          const resourceMap: Record<
            string,
            Record<string, Array<{ code: string; description: string }>>
          > = {};

          group.permissions.forEach((permission) => {
            const { resource, scope } = getResourceAndScope(permission.code);
            if (!resource || !scope) return;

            if (!resourceMap[resource]) {
              resourceMap[resource] = {};
            }

            if (!resourceMap[resource][scope]) {
              resourceMap[resource][scope] = [];
            }

            resourceMap[resource][scope].push(permission);
          });

          return (
            <PermissionGroup
              key={group.group}
              group={group}
              groupApplicableActions={groupApplicableActions}
              resourceMap={resourceMap}
              selectedPermissions={selectedPermissions}
              onSelectPermission={onSelectPermission}
            />
          );
        })}
      </div>
      <ExportPermissionsSection
        exportPermissions={exportPermissions}
        selectedPermissions={selectedPermissions}
        onSelectPermission={onSelectPermission}
      />
    </div>
  );
};

export default PermissionTable;
